import { useCallback } from 'react';
import { useFeatureGate, useExperiment, useDynamicConfig, useStatsigClient } from '@statsig/react-bindings';
import { createLogger } from '../utils/logger-ssr-safe';

const logger = createLogger({ module: 'StatsigFeatures', level: 'info' });

// Notification system features
export function useNotificationFeatures() {
  const unifiedNotifications = useFeatureGate('unified_notification_system');
  const bannerNotifications = useFeatureGate('banner_notifications');
  const bellNotifications = useFeatureGate('bell_notifications');
  const realTimeNotifications = useFeatureGate('real_time_notifications');
  
  const notificationExperiment = useExperiment('notification_content_test');
  const statsigClient = useStatsigClient();

  // Use direct Statsig client - but handle case when it's not available yet
  const logNotificationEvent = (eventName: string, metadata?: Record<string, unknown>) => {
    if (statsigClient) {
      statsigClient.logEvent(eventName, undefined, {
        timestamp: Date.now().toString(),
        feature_category: 'notifications',
        ...metadata
      });
    } else {
      // Statsig not available yet - this is expected during app initialization
      logger.debug('Statsig client not available for notification event', { eventName });
    }
  };

  return {
    features: {
      unifiedNotifications: unifiedNotifications.value,
      bannerNotifications: bannerNotifications.value,
      bellNotifications: bellNotifications.value,
      realTimeNotifications: realTimeNotifications.value
    },
    loading: {
      unifiedNotifications: false, // Feature gates don't have loading states in this API
      bannerNotifications: false,
      bellNotifications: false,
      realTimeNotifications: false
    },
    experiment: notificationExperiment,
    logEvent: logNotificationEvent
  };
}

// UX enhancement features
export function useUXFeatures() {
  const newOnboarding = useFeatureGate('new_onboarding_flow');
  const enhancedDashboard = useFeatureGate('enhanced_home_dashboard');
  const improvedNavigation = useFeatureGate('improved_navigation');
  const mobileOptimizations = useFeatureGate('mobile_optimizations');

  const onboardingExperiment = useExperiment('onboarding_banner_test');

  return {
    features: {
      newOnboarding: newOnboarding.value,
      enhancedDashboard: enhancedDashboard.value,
      improvedNavigation: improvedNavigation.value,
      mobileOptimizations: mobileOptimizations.value
    },
    loading: {
      newOnboarding: false,
      enhancedDashboard: false,
      improvedNavigation: false,
      mobileOptimizations: false
    },
    experiments: {
      onboarding: onboardingExperiment
    }
  };
}

// Welcome Onboarding Tour feature gate
// Configure in Statsig: Beta 100%, New signups 50%, All users 0%
export function useWelcomeOnboardingTour() {
  const welcomeTour = useFeatureGate('welcome_onboarding_tour');
  const statsigClient = useStatsigClient();

  const logTourEvent = useCallback((eventName: string, metadata?: Record<string, unknown>) => {
    if (!statsigClient) {
      return;
    }

    statsigClient.logEvent(eventName, undefined, {
      timestamp: Date.now().toString(),
      feature_category: 'onboarding',
      ...metadata
    });
  }, [statsigClient]);

  return {
    enabled: welcomeTour.value,
    logEvent: logTourEvent
  };
}

// App configuration
export function useAppConfig() {
  const appConfig = useDynamicConfig('app_configuration');
  
  return {
    maxNotificationsPerHour: appConfig.get('max_notifications_per_hour', 5),
    onboardingSteps: appConfig.get('onboarding_steps', 3),
    featureRolloutPercentage: appConfig.get('feature_rollout_percentage', 10),
    maintenanceMode: appConfig.get('maintenance_mode', false),
    apiTimeout: appConfig.get('api_timeout_ms', 5000),
    enableDebugMode: appConfig.get('enable_debug_mode', false)
  };
}

// Business metrics tracking
export function useBusinessMetrics() {
  // IMPORTANT: All hooks must be called unconditionally at the top level
  // Call useStatsigClient without try-catch
  const statsigClient = useStatsigClient();

  // Use direct Statsig client - but handle case when it's not available yet
  const trackBusinessMetric = useCallback((metric: string, value: number, metadata?: Record<string, unknown>) => {
    if (!statsigClient) {
      // Don't log anything - this is expected during initialization
      return;
    }

    // Add debug logging to track which user ID is being used
    logger.debug('Tracking business metric', {
      metric,
      userID: (statsigClient as { getStableID?: () => string })?.getStableID?.() || 'unknown',
      hasMetadata: !!metadata
    });
    statsigClient.logEvent('business_metric', value, {
      metric_name: metric,
      metric_value: String(value),
      ...metadata
    });
  }, [statsigClient]);

  const trackFeatureUsage = useCallback((featureName: string, action: string, metadata?: Record<string, unknown>) => {
    if (!statsigClient) {
      // Don't log anything - this is expected during initialization
      return;
    }

    // Add debug logging to track which user ID is being used
    logger.debug('Tracking feature usage', {
      featureName,
      action,
      userID: (statsigClient as { getStableID?: () => string })?.getStableID?.() || 'unknown',
      hasMetadata: !!metadata
    });
    statsigClient.logEvent('feature_usage', undefined, {
      feature_name: featureName,
      action,
      ...metadata
    });
  }, [statsigClient]);

  const trackUserJourney = useCallback((step: string, metadata?: Record<string, unknown>) => {
    if (!statsigClient) {
      // Don't log anything - this is expected during initialization
      return;
    }

    // Add debug logging to track which user ID is being used
    logger.debug('Tracking user journey', {
      step,
      userID: (statsigClient as { getStableID?: () => string })?.getStableID?.() || 'unknown',
      hasMetadata: !!metadata
    });
    statsigClient.logEvent('user_journey', undefined, {
      journey_step: step,
      ...metadata
    });
  }, [statsigClient]);

  return {
    trackBusinessMetric,
    trackFeatureUsage,
    trackUserJourney
  };
}

// Career card features
export function useCareerCardFeatures() {
  const aiSuggestions = useFeatureGate('ai_career_suggestions');
  const bulkOperations = useFeatureGate('bulk_card_operations');
  const advancedFiltering = useFeatureGate('advanced_card_filtering');
  const cardTemplates = useFeatureGate('career_card_templates');
  
  return {
    features: {
      aiSuggestions: aiSuggestions.value,
      bulkOperations: bulkOperations.value,
      advancedFiltering: advancedFiltering.value,
      cardTemplates: cardTemplates.value
    },
    loading: {
      aiSuggestions: false,
      bulkOperations: false,
      advancedFiltering: false,
      cardTemplates: false
    }
  };
}

// File management features
export function useFileFeatures() {
  const googleDriveIntegration = useFeatureGate('google_drive_integration');
  const bulkFileOperations = useFeatureGate('bulk_file_operations');
  const filePreview = useFeatureGate('enhanced_file_preview');
  const fileVersioning = useFeatureGate('file_versioning');
  
  return {
    features: {
      googleDriveIntegration: googleDriveIntegration.value,
      bulkFileOperations: bulkFileOperations.value,
      filePreview: filePreview.value,
      fileVersioning: fileVersioning.value
    },
    loading: {
      googleDriveIntegration: false,
      bulkFileOperations: false,
      filePreview: false,
      fileVersioning: false
    }
  };
}

// Analytics and insights features
export function useAnalyticsFeatures() {
  const advancedAnalytics = useFeatureGate('advanced_analytics_dashboard');
  const exportFeatures = useFeatureGate('data_export_features');
  const realTimeInsights = useFeatureGate('real_time_insights');
  
  return {
    features: {
      advancedAnalytics: advancedAnalytics.value,
      exportFeatures: exportFeatures.value,
      realTimeInsights: realTimeInsights.value
    },
    loading: {
      advancedAnalytics: false,
      exportFeatures: false,
      realTimeInsights: false
    }
  };
}

// Search features (including hybrid/semantic search)
export function useSearchFeatures() {
  const hybridSearch = useFeatureGate('hybrid_search_enabled');
  const semanticSearch = useFeatureGate('semantic_search_enabled');
  const ragSearch = useFeatureGate('rag_search_enabled');

  return {
    features: {
      hybridSearch: hybridSearch.value,
      semanticSearch: semanticSearch.value,
      ragSearch: ragSearch.value
    },
    loading: {
      hybridSearch: false,
      semanticSearch: false,
      ragSearch: false
    }
  };
}

// Career Focus features
export function useCareerFocusFeatures() {
  const careerFocus = useFeatureGate('enable_career_focus');
  const internshipFocus = useFeatureGate('enable_internship_focus');
  const performanceReview = useFeatureGate('enable_performance_review');
  const rubricSystem = useFeatureGate('enable_rubric_system');
  const focusNotifications = useFeatureGate('enable_focus_notifications');
  const internshipDashboardV2 = useFeatureGate('internshipdashboardv2');
  const internshipReviewPrep = useFeatureGate('internship_review_prep');

  // IMPORTANT: All hooks must be called unconditionally at the top level
  const statsigClient = useStatsigClient();

  const logFocusEvent = (eventName: string, metadata?: Record<string, unknown>) => {
    if (!statsigClient) {
      return;
    }

    statsigClient.logEvent(eventName, undefined, {
      timestamp: Date.now().toString(),
      feature_category: 'career_focus',
      ...metadata
    });
  };
  
  return {
    features: {
      careerFocus: careerFocus.value,
      internshipFocus: internshipFocus.value,
      performanceReview: performanceReview.value,
      rubricSystem: rubricSystem.value,
      focusNotifications: focusNotifications.value,
      internshipDashboardV2: internshipDashboardV2.value,
      internshipReviewPrep: internshipReviewPrep.value
    },
    loading: {
      careerFocus: false,
      internshipFocus: false,
      performanceReview: false,
      rubricSystem: false,
      focusNotifications: false,
      internshipDashboardV2: false,
      internshipReviewPrep: false
    },
    logEvent: logFocusEvent
  };
} 